<script setup lang="ts">
import { useAdminGetAiJob } from '@swipe4work/api-orval'
import JobAgentPanel from '@/features/tools/components/JobAgentPanel.vue'
import JobAgentCompanySelect from '@/features/tools/components/job-agent/JobAgentCompanySelect.vue'

definePage({
  path: '/tools/AiAgents',
  name: '/tools/AiAgents',
  meta: {
    title: 'AI-agents testen',
    permission: {
      resource: 'Job',
      level: 'Read',
    },
  },
})

/*
 * A vacancy always belongs to a company, so the page starts with picking one (or arrives with
 * ?companyId= preselected, e.g. from a company page) and only then shows the agent workspace.
 * It can also open on a saved AiJob (?aiJobId=), which is loaded back into the workspace to refine it.
 */
const route = useRoute('/tools/AiAgents')
const router = useRouter()

function queryNumber(value: unknown): number | null {
  const parsed = Number(Array.isArray(value) ? value[0] : value)

  return Number.isInteger(parsed) && parsed > 0 ? parsed : null
}

const companyId = ref<number | null>(queryNumber(route.query.companyId))
const aiJobId = computed(() => queryNumber(route.query.aiJobId))
const isEditingAiJob = computed(() => aiJobId.value != null)

const { data: aiJob, isPending: aiJobPending, isError: aiJobError } = useAdminGetAiJob(
  () => aiJobId.value ?? 0,
  {
    query: {
      enabled: isEditingAiJob,
      staleTime: 0,
    },
  },
)

// The AiJob fixes the company; the workspace mounts once both are known (see the v-if below).
watch(aiJob, (loaded) => {
  if (loaded) companyId.value = loaded.companyId
}, { immediate: true })

const aiJobReady = computed(() => !isEditingAiJob.value || (aiJob.value != null && aiJob.value.id === aiJobId.value))

/*
 * The workspace mounts once the company (and, when editing, the AiJob) is known and then stays
 * mounted: the first save puts the new id in the URL, which must not reload it and lose the chat
 * history. Navigating to a *different* AiJob (or back to a fresh page) does remount it.
 */
const panelMounted = ref(false)
const panelAiJobId = ref(aiJobId.value)
const lastSavedAiJobId = ref<number | null>(null)

watch(aiJobId, (id) => {
  if (id === lastSavedAiJobId.value) return
  panelAiJobId.value = id
  panelMounted.value = false
})

const showPanel = computed(() => companyId.value != null && (panelMounted.value || aiJobReady.value))

watch(showPanel, (shown) => {
  if (shown) panelMounted.value = true
}, { immediate: true })

const isLoadingAiJob = computed(() => isEditingAiJob.value && aiJobPending.value && !panelMounted.value)
const panelKey = computed(() => `${companyId.value}-${panelAiJobId.value ?? 'new'}`)

// After the first save the URL carries the AiJob id, so a reload continues on the saved one.
function onSaved(savedAiJobId: number) {
  if (aiJobId.value === savedAiJobId) return
  lastSavedAiJobId.value = savedAiJobId
  router.replace({
    name: '/tools/AiAgents',
    query: {
      ...route.query,
      aiJobId: String(savedAiJobId),
    },
  })
}
</script>

<template>
  <div class="flex flex-col gap-4">
    <Message v-if="aiJobError" severity="error">
      De AI-vacature kon niet worden geladen.
      <RouterLink :to="{ name: 'ai-jobs.overview' }" class="ml-1 font-medium underline">
        Naar het overzicht
      </RouterLink>
    </Message>

    <div v-else-if="isLoadingAiJob" class="flex flex-col gap-4">
      <Skeleton height="8rem" />
      <Skeleton v-for="n in 3" :key="n" height="6rem" />
    </div>

    <!-- Start state: the workspace needs a company before the agent can do anything -->
    <Card v-else-if="companyId == null">
      <template #content>
        <div class="flex flex-col gap-4 md:max-w-xl">
          <div class="flex items-start gap-3">
            <i class="fa-regular fa-robot mt-1 text-xl text-primary-500" />
            <div class="flex flex-col gap-1">
              <h2 class="text-surface-900 font-bold">
                Nieuwe AI-vacature
              </h2>
              <p class="text-sm text-surface-500">
                Kies het bedrijf waarvoor je een vacature wilt genereren. Daarna plak je de vacaturetekst,
                laat je de agent de data bepalen en de tekst schrijven, en sla je het resultaat op als AI-vacature.
              </p>
            </div>
          </div>
          <JobAgentCompanySelect v-model="companyId" />
        </div>
      </template>
    </Card>

    <!-- Remount the workspace (result + chat history) when another company or AiJob is selected. -->
    <JobAgentPanel
      v-else-if="showPanel"
      :key="panelKey"
      v-model:company-id="companyId"
      :ai-job="aiJob ?? null"
      @saved="onSaved"
    />
  </div>
</template>
